import type { Media, VideoDetail } from 'models';
import { useTranslation } from 'react-i18next';
import { Col, Form, Icon, Row, Tooltip, type UploadFile, VideoDragger } from 'ui';
import { useFormFileUpload } from './hooks';

type P = {
  fullData?: Media;
  trailerData?: Media;
  video: VideoDetail;
};

type VideoUploadFormValues = {
  videoFull: [UploadFile];
  videoTrailer: [UploadFile];
};

const VideoUploadForm = ({ fullData, trailerData, video }: P) => {
  const [form] = Form.useForm<VideoUploadFormValues>();

  const { t } = useTranslation();

  const {
    initialValue: trailerInitialValue,
    removeUpload: removeUploadTrailer,
    retryUpload: retryUploadTrailer,
    startUpload: startUploadTrailer,
  } = useFormFileUpload({
    contentType: 'content_object',
    form,
    formName: 'videoTrailer',
    mediaData: trailerData,
    ref: video.id,
    type: 'trailer',
  });

  const {
    initialValue: videoInitialValue,
    removeUpload: removeUploadFull,
    retryUpload: retryUploadFull,
    startUpload: startUploadFull,
  } = useFormFileUpload({
    contentType: 'content_object',
    form,
    formName: 'videoFull',
    mediaData: fullData,
    ref: video.id,
    type: 'full',
  });

  const initialValues: Partial<VideoUploadFormValues> = {
    videoFull: videoInitialValue,
    videoTrailer: trailerInitialValue,
  };

  return (
    <Form<VideoUploadFormValues> form={form} initialValues={initialValues}>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item<VideoUploadFormValues>
            label={
              <Row gutter={['xs', 0]}>
                <Col>{t('Trailer')}</Col>
                <Col>
                  <Tooltip title="Upload a short video to get members interested. Members can always play this video for free.">
                    <Icon icon="question-line" />
                  </Tooltip>
                </Col>
              </Row>
            }
            name="videoTrailer"
            rules={[
              {
                required: true,
              },
            ]}
            valuePropName="fileList"
          >
            <VideoDragger
              removeUpload={removeUploadTrailer}
              retryUpload={retryUploadTrailer}
              startUpload={startUploadTrailer}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item<VideoUploadFormValues>
            label={
              <Row gutter={['xs', 0]}>
                <Col>{t('Video')}</Col>
                <Col>
                  <Tooltip title="Upload the full-length video here. If you've decided to add a price for the video, members will have to pay before they can view this video.">
                    <Icon icon="question-line" />
                  </Tooltip>
                </Col>
              </Row>
            }
            name="videoFull"
            rules={[
              {
                required: true,
              },
            ]}
            valuePropName="fileList"
          >
            <VideoDragger removeUpload={removeUploadFull} retryUpload={retryUploadFull} startUpload={startUploadFull} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default VideoUploadForm;
